/**
 * Debug Panel Component
 *
 * Visual panel showing BroadcastChannel message logs and sync status
 */

import { broadcastDebugger } from '../utils/broadcast-debug.js';

class DebugPanel extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.visible = false;
    this.filter = 'all'; // 'all', 'sent', 'received'
    this.autoScroll = true;
    this.unsubscribe = null;
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();

    // Subscribe to new log entries
    this.unsubscribe = broadcastDebugger.onLogEntry((entry) => {
      if (entry === null) {
        // Clear signal
        this.refreshLogs();
      } else {
        this.addLogEntryToUI(entry);
      }
    });

    // Initial load
    this.refreshLogs();
  }

  disconnectedCallback() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          position: fixed;
          bottom: 0;
          right: 0;
          width: 600px;
          max-height: 400px;
          background: #1e1e1e;
          color: #d4d4d4;
          font-family: 'Courier New', monospace;
          font-size: 12px;
          border: 1px solid #3c3c3c;
          border-bottom: none;
          border-right: none;
          box-shadow: -2px -2px 10px rgba(0,0,0,0.5);
          z-index: 10000;
          display: flex;
          flex-direction: column;
          transform: ${this.visible ? 'translateY(0)' : 'translateY(100%)'};
          transition: transform 0.3s ease;
        }

        .header {
          background: #2d2d2d;
          padding: 8px 12px;
          border-bottom: 1px solid #3c3c3c;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }

        .title {
          font-weight: bold;
          color: #4ec9b0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        button {
          background: #3c3c3c;
          color: #d4d4d4;
          border: 1px solid #555;
          padding: 4px 8px;
          cursor: pointer;
          font-size: 11px;
          border-radius: 3px;
        }

        button:hover {
          background: #4c4c4c;
        }

        button.active {
          background: #007acc;
          border-color: #007acc;
        }

        .stats {
          font-size: 10px;
          color: #858585;
        }

        .logs {
          flex: 1;
          overflow-y: auto;
          padding: 8px;
        }

        .log-entry {
          margin-bottom: 8px;
          padding: 6px;
          background: #252526;
          border-left: 3px solid #555;
          border-radius: 2px;
        }

        .log-entry.sent {
          border-left-color: #4CAF50;
        }

        .log-entry.received {
          border-left-color: #2196F3;
        }

        .log-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
          font-size: 11px;
        }

        .log-type {
          font-weight: bold;
        }

        .log-type.sent {
          color: #4CAF50;
        }

        .log-type.received {
          color: #2196F3;
        }

        .log-time {
          color: #858585;
        }

        .log-payload {
          font-size: 11px;
          color: #ce9178;
          margin-left: 12px;
          max-height: 100px;
          overflow: auto;
        }

        .log-state {
          font-size: 10px;
          color: #858585;
          margin-left: 12px;
          margin-top: 4px;
        }

        .empty {
          text-align: center;
          padding: 40px;
          color: #858585;
        }

        .footer {
          background: #2d2d2d;
          padding: 6px 12px;
          border-top: 1px solid #3c3c3c;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
          font-size: 10px;
        }

        .sync-indicator {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .sync-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #555;
        }

        .sync-dot.active {
          background: #4CAF50;
          box-shadow: 0 0 6px #4CAF50;
          animation: pulse 1s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .filter-buttons {
          display: flex;
          gap: 4px;
        }

        .filter-buttons button {
          padding: 2px 6px;
          font-size: 10px;
        }
      </style>

      <div class="header">
        <div class="title">
          📡 BroadcastChannel Debug
        </div>
        <div class="controls">
          <div class="stats">
            <span id="stats-text">0 messages</span>
          </div>
          <button id="clear-btn" title="Clear logs">Clear</button>
          <button id="export-btn" title="Export logs as JSON">Export</button>
          <button id="close-btn" title="Close panel (Ctrl+Shift+D)">✕</button>
        </div>
      </div>

      <div class="logs" id="logs-container">
        <div class="empty">No messages logged yet. Press Ctrl+Shift+D to toggle this panel.</div>
      </div>

      <div class="footer">
        <div class="sync-indicator">
          <div class="sync-dot" id="sync-dot"></div>
          <span id="sync-status">Ready</span>
        </div>
        <div class="filter-buttons">
          <button id="filter-all" class="active">All</button>
          <button id="filter-sent">Sent</button>
          <button id="filter-received">Received</button>
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    const clearBtn = this.shadowRoot.getElementById('clear-btn');
    const exportBtn = this.shadowRoot.getElementById('export-btn');
    const closeBtn = this.shadowRoot.getElementById('close-btn');
    const filterAll = this.shadowRoot.getElementById('filter-all');
    const filterSent = this.shadowRoot.getElementById('filter-sent');
    const filterReceived = this.shadowRoot.getElementById('filter-received');

    clearBtn.addEventListener('click', () => {
      broadcastDebugger.clear();
      this.refreshLogs();
    });

    exportBtn.addEventListener('click', () => {
      broadcastDebugger.downloadLogs();
    });

    closeBtn.addEventListener('click', () => {
      this.toggle();
    });

    filterAll.addEventListener('click', () => {
      this.setFilter('all');
    });

    filterSent.addEventListener('click', () => {
      this.setFilter('sent');
    });

    filterReceived.addEventListener('click', () => {
      this.setFilter('received');
    });
  }

  setFilter(filter) {
    this.filter = filter;

    // Update button states
    const buttons = this.shadowRoot.querySelectorAll('.filter-buttons button');
    buttons.forEach(btn => btn.classList.remove('active'));

    const activeBtn = this.shadowRoot.getElementById(`filter-${filter}`);
    if (activeBtn) {
      activeBtn.classList.add('active');
    }

    this.refreshLogs();
  }

  refreshLogs() {
    const container = this.shadowRoot.getElementById('logs-container');

    const filterObj = this.filter === 'all' ? {} : { direction: this.filter };
    const logs = broadcastDebugger.getFilteredLogs(filterObj);

    if (logs.length === 0) {
      container.innerHTML = '<div class="empty">No messages to display.</div>';
      this.updateStats(0);
      return;
    }

    container.innerHTML = '';
    logs.forEach(log => this.addLogEntryToUI(log, false));

    this.updateStats(logs.length);

    if (this.autoScroll) {
      container.scrollTop = container.scrollHeight;
    }
  }

  addLogEntryToUI(entry, shouldScroll = true) {
    // Check if entry matches current filter
    if (this.filter !== 'all' && entry.direction !== this.filter) {
      return;
    }

    const container = this.shadowRoot.getElementById('logs-container');

    // Remove empty message if present
    const empty = container.querySelector('.empty');
    if (empty) {
      empty.remove();
    }

    const logDiv = document.createElement('div');
    logDiv.className = `log-entry ${entry.direction}`;

    const time = new Date(entry.timestamp).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });

    const arrow = entry.direction === 'sent' ? '→' : '←';
    const payloadStr = JSON.stringify(entry.data, null, 2);
    const stateStr = entry.sessionState
      ? `Events: ${entry.sessionState.currentEventId} | History: ${entry.sessionState.historyLength} | Revealed: ${entry.sessionState.revealedCount} | Undo: ${entry.sessionState.undoStackLength} | Redo: ${entry.sessionState.redoStackLength}`
      : 'No session state';

    logDiv.innerHTML = `
      <div class="log-header">
        <span class="log-type ${entry.direction}">${arrow} ${entry.type}</span>
        <span class="log-time">${time}</span>
      </div>
      <div class="log-payload">${payloadStr}</div>
      <div class="log-state">${stateStr}</div>
    `;

    container.appendChild(logDiv);

    // Update stats
    const stats = broadcastDebugger.getStats();
    this.updateStats(stats.total);

    // Flash sync indicator
    this.flashSyncIndicator();

    if (shouldScroll && this.autoScroll) {
      container.scrollTop = container.scrollHeight;
    }
  }

  updateStats(count) {
    const statsText = this.shadowRoot.getElementById('stats-text');
    if (statsText) {
      statsText.textContent = `${count} message${count !== 1 ? 's' : ''}`;
    }
  }

  flashSyncIndicator() {
    const dot = this.shadowRoot.getElementById('sync-dot');
    const status = this.shadowRoot.getElementById('sync-status');

    if (dot && status) {
      dot.classList.add('active');
      status.textContent = 'Syncing...';

      setTimeout(() => {
        dot.classList.remove('active');
        status.textContent = 'Ready';
      }, 1000);
    }
  }

  toggle() {
    this.visible = !this.visible;
    this.style.transform = this.visible ? 'translateY(0)' : 'translateY(100%)';

    if (this.visible) {
      this.refreshLogs();
    }
  }

  show() {
    this.visible = true;
    this.style.transform = 'translateY(0)';
    this.refreshLogs();
  }

  hide() {
    this.visible = false;
    this.style.transform = 'translateY(100%)';
  }
}

customElements.define('debug-panel', DebugPanel);

export default DebugPanel;
